import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doctorService } from '../services/doctorService';
import { doctorAssistantService, type DoctorAssistantAssignment } from '../../../services/doctorAssistantService';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Spinner from '../../../components/ui/Spinner';

interface DoctorDetail {
  id: number;
  first_name: string;
  last_name: string;
  alias?: string;
  email?: string;
  specialty?: { id: number; name: string };
  phone?: string;
  mobile?: string;
  extension?: string;
  is_active: boolean;
  user_id?: number | null;
  created_at?: string;
}

const DoctorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [doctor, setDoctor] = useState<DoctorDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessResult, setAccessResult] = useState<{ message: string; temp_password?: string; type: 'success' | 'error' } | null>(null);
  const [showAccessConfirm, setShowAccessConfirm] = useState(false);
  const [assistants, setAssistants] = useState<DoctorAssistantAssignment[]>([]);
  const [assistantsLoading, setAssistantsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    doctorAssistantService.listByDoctor(parseInt(id))
      .then(setAssistants)
      .catch(() => setAssistants([]))
      .finally(() => setAssistantsLoading(false));
  }, [id]);

  useEffect(() => {
    const fetchDoctor = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await doctorService.getById(parseInt(id));
        setDoctor(data);
      } catch (err) {
        console.error('Error fetching doctor:', err);
        setError('Error loading doctor');
      } finally {
        setLoading(false);
      }
    };

    fetchDoctor();
  }, [id]);

  const requestCreateAccess = () => {
    if (!id || !doctor) return;
    setShowAccessConfirm(true);
  };

  const cancelAccessConfirm = () => {
    if (accessLoading) return;
    setShowAccessConfirm(false);
  };

  const handleCreateAccess = async () => {
    if (!id || !doctor) return;
    setAccessLoading(true);
    setAccessResult(null);
    try {
      const response = await doctorService.createAccess(parseInt(id));
      setDoctor(prev => prev ? { ...prev, user_id: response.user_id ?? prev.user_id ?? -1 } : prev);
      setAccessResult({
        type: 'success',
        message: response.message,
        temp_password: response.temp_password,
      });
      setShowAccessConfirm(false);
    } catch (err: any) {
      setAccessResult({
        type: 'error',
        message: err?.response?.data?.message || 'Error al crear el acceso',
      });
    } finally {
      setAccessLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 text-center">
            <Spinner variant="full-page" text="Cargando..." size="lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="alert alert-danger">
              {error || 'Médico no encontrado'}
              <Link to="/doctors" className="btn btn-outline-secondary btn-sm float-end">
                Volver
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasAccess = !!doctor.user_id || accessResult?.type === 'success';

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fa fa-user-md"></i> Detalles del Médico
              </h3>
              <div className="card-tools">
                <Link to={`/doctors/${doctor.id}/edit`} className="btn btn-primary btn-sm me-1">
                  <i className="fa fa-edit"></i> Editar
                </Link>
                <Link to="/doctors" className="btn btn-outline-secondary btn-sm">
                  <i className="fa fa-arrow-left"></i> Volver
                </Link>
              </div>
            </div>

            <div className="card-body">
              {/* Doctor Header */}
              <div className="row" style={{ marginBottom: '20px' }}>
                <div className="col-md-3">
                  <div className="text-center">
                    <i className="fa fa-user-md fa-5x"></i>
                    <h4>{doctor.first_name} {doctor.last_name}</h4>
                    <span className={`badge ${doctor.is_active ? 'bg-success' : 'bg-secondary'}`}>
                      {doctor.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
                <div className="col-md-9">
                  <div className="row">
                    <div className="col-md-4">
                      <strong>Código:</strong> {doctor.alias || `MED-${doctor.id}`}
                    </div>
                    <div className="col-md-4">
                      <strong>Especialidad:</strong> {doctor.specialty?.name || '-'}
                    </div>
                    <div className="col-md-4">
                      <strong>Email:</strong> {doctor.email || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="row">
                <div className="col-md-6">
                  <h4>Información de Contacto</h4>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td><strong>Teléfono:</strong></td>
                        <td>{doctor.phone || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Celular:</strong></td>
                        <td>{doctor.mobile || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Extensión:</strong></td>
                        <td>{doctor.extension || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h4>Información del Sistema</h4>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td><strong>Fecha de Creación:</strong></td>
                        <td>{doctor.created_at ? new Date(doctor.created_at).toLocaleDateString('es-DO') : '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>ID:</strong></td>
                        <td>{doctor.id}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* System Access Section */}
              <div className="row mt-3">
                <div className="col-md-12">
                  <div className={`card border-${hasAccess ? 'success' : 'warning'}`}>
                    <div className={`card-header bg-${hasAccess ? 'success' : 'warning'} text-white`}>
                      <h5 className="mb-0">
                        <i className={`fa fa-${hasAccess ? 'check-circle' : 'lock'} me-2`}></i>
                        Acceso al Sistema
                      </h5>
                    </div>
                    <div className="card-body">
                      {hasAccess ? (
                        <div className="d-flex align-items-center gap-2">
                          <i className="fa fa-check-circle text-success fa-2x me-2"></i>
                          <div>
                            <strong>Este doctor tiene acceso al sistema</strong>
                            {doctor.email && <p className="mb-0 text-muted">Acceso con: {doctor.email}</p>}
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <strong>Sin acceso al sistema</strong>
                            <p className="mb-0 text-muted">
                              {doctor.email
                                ? 'Al crear el acceso, se enviará un email con las credenciales al doctor.'
                                : 'Este doctor no tiene email registrado. Agregue uno antes de crear el acceso.'}
                            </p>
                          </div>
                          <button
                            className="btn btn-warning"
                            onClick={requestCreateAccess}
                            disabled={accessLoading || !doctor.email}
                          >
                            {accessLoading
                              ? <><i className="fa fa-spinner fa-spin me-1"></i> Creando...</>
                              : <><i className="fa fa-key me-1"></i> Crear Acceso</>
                            }
                          </button>
                        </div>
                      )}

                      {accessResult && (
                        <div className={`alert alert-${accessResult.type === 'success' ? 'success' : 'danger'} mt-3 mb-0`}>
                          <strong>{accessResult.message}</strong>
                          {accessResult.temp_password && (
                            <div className="mt-2">
                              <span>Contraseña temporal: </span>
                              <code className="fs-6">{accessResult.temp_password}</code>
                              <small className="d-block text-muted mt-1">
                                Comparta esta contraseña con el doctor. Deberá cambiarla en el primer ingreso.
                              </small>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Asistentes (clinical-delegation R2, task 3.9) — list-only;
                  grant/revoke stays on its own admin screen. */}
              <div className="row mt-3">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="mb-0"><i className="fa fa-user-nurse me-2"></i>Asistentes</h5>
                    </div>
                    <div className="card-body p-0">
                      {assistantsLoading ? (
                        <div className="p-3"><Spinner text="Cargando asistentes..." /></div>
                      ) : assistants.length === 0 ? (
                        <p className="text-muted p-3 mb-0">Este médico no tiene asistentes asignados.</p>
                      ) : (
                        <table className="table table-borderless mb-0">
                          <tbody>
                            {assistants.map((a) => (
                              <tr key={a.id}>
                                <td>
                                  {a.assistant_user
                                    ? `${a.assistant_user.name} ${a.assistant_user.lastname || ''}`.trim()
                                    : `Usuario #${a.assistant_user_id}`}
                                </td>
                                <td className="text-muted">
                                  Asignado el {new Date(a.granted_at).toLocaleDateString('es-DO')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={showAccessConfirm}
        title="Crear acceso al sistema"
        body={`¿Crear acceso al sistema para Dr. ${doctor.first_name} ${doctor.last_name}?`}
        confirmLabel="Crear acceso"
        confirmVariant="primary"
        loading={accessLoading}
        onConfirm={handleCreateAccess}
        onCancel={cancelAccessConfirm}
      />
    </div>
  );
};

export default DoctorDetailPage;
